import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { TranslationKeys } from 'app/translations';
import type { BulkLeftCompanyEmployee, BulkLeftCompanyBackupResponse } from '../../Services';
import { formatIsoDate, formatBackendDate } from './utils';

// ─── Types ────────────────────────────────────────────────────────────────────

type BackupPreviewProps = {
  data: BulkLeftCompanyBackupResponse;
  csvEmployees: BulkLeftCompanyEmployee[];
};

// ─── Component ────────────────────────────────────────────────────────────────

export const BackupPreview = ({ data, csvEmployees }: BackupPreviewProps) => {
  const intl = useIntl();

  /** Map connectionId:bsn → new endDate (dd-mm-yyyy) from CSV */
  const newEndDateByKey = useMemo(() => {
    const map = new Map<string, string>();
    csvEmployees.forEach(e => map.set(`${e.connectionId}:${e.bsn}`, e.endDate));
    return map;
  }, [csvEmployees]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Backup info banner */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          bgcolor: 'success.50',
          borderColor: 'success.light',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_backupBanner })}
            color="success"
            size="small"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_backupId })}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {data.backupId}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_backupDate })}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatIsoDate(data.backupDate)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Employees */}
      <Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {intl.formatMessage(
            { id: TranslationKeys.adminPanel_bulkLeftCompany_preview_employees },
            { count: data.employees.length },
          )}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colId })}
                </TableCell>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colConnectionId })}
                </TableCell>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colBsn })}
                </TableCell>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colStartDate })}
                </TableCell>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colEndDatePrevious })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.employees.map(emp => {
                const newEndDate = newEndDateByKey.get(`${emp.connectionId}:${emp.bsn}`) ?? null;
                const hasDiff = newEndDate !== null;
                return (
                  <TableRow key={emp.id}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{emp.id}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{emp.connectionId}</TableCell>
                    <TableCell>{emp.bsn}</TableCell>
                    <TableCell>{formatIsoDate(emp.startDateOfEmployment)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {hasDiff && emp.endDateOfEmployment ? (
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ textDecoration: 'line-through', color: 'text.disabled' }}
                          >
                            {formatIsoDate(emp.endDateOfEmployment)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" component="span" sx={{ color: 'text.disabled' }}>
                            {formatIsoDate(emp.endDateOfEmployment)}
                          </Typography>
                        )}
                        {hasDiff && (
                          <Chip
                            label={formatBackendDate(newEndDate)}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Divider />

      {/* Employments */}
      <Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {intl.formatMessage(
            { id: TranslationKeys.adminPanel_bulkLeftCompany_preview_employments },
            { count: data.employments.length },
          )}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colId })}
                </TableCell>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colStartDate })}
                </TableCell>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colEndDate })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.employments.map(emp => {
                return (
                  <TableRow key={emp.id}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{emp.id}</TableCell>
                    <TableCell>{formatIsoDate(emp.startDate)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" component="span">
                          {formatIsoDate(emp.endDate)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Divider />

      {/* Synchronizations */}
      <Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {intl.formatMessage(
            { id: TranslationKeys.adminPanel_bulkLeftCompany_preview_synchronizations },
            { count: data.synchronizations.length },
          )}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colId })}
                </TableCell>
                <TableCell>
                  {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_preview_colStatusEmployee })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.synchronizations.map(sync => (
                <TableRow key={sync.id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{sync.id}</TableCell>
                  <TableCell>
                    <Chip
                      label={String(sync.statusEmployee)}
                      color={sync.statusEmployee ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
